// Case progress lives on the server (GET/PATCH /v1/applications/{id}/progress)
// so a case looks the same on every device and browser. localStorage
// ("sp-done", "sp-scan-result", "sp-scholar-url") is only a cache the
// existing pages read synchronously; syncCaseProgress() refreshes it.

import { applications, type ApplicationOut, type CaseProgressOut, type CaseProgressPatch, type CaseSetup } from "./api";
import { readScanResult, writeScanResult, type ScanResult } from "./scanResult";

const DONE_KEY = "sp-done";
const SCHOLAR_URL_KEY = "sp-scholar-url";
// Set once this browser's pre-server progress has been uploaded for a case.
const syncedKey = (appId: string) => `sp-synced:${appId}`;

/** The workspace's EB-1A application (one case per workspace). */
export async function caseApplication(): Promise<ApplicationOut | null> {
  const apps = await applications.list();
  if (!Array.isArray(apps)) return null;
  return apps.find((a) => a.application_type === "eb1a") ?? apps[0] ?? null;
}

function readLocalDone(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(DONE_KEY) || "[]");
    return Array.isArray(raw) ? raw.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function writeLocal(p: CaseProgressOut): void {
  try {
    localStorage.setItem(DONE_KEY, JSON.stringify(p.done_stages));
    if (p.scholar_url) localStorage.setItem(SCHOLAR_URL_KEY, p.scholar_url);
  } catch {
    /* private mode */
  }
  if (p.scan_result) writeScanResult(p.scan_result as ScanResult);
}

/** True when the server shows the person has been through the eligibility
 *  read or past it, so they belong on the Case Plan, not at step 1. */
export function caseHasStarted(p: CaseProgressOut | null): boolean {
  return !!p && (!!p.scan_result || p.done_stages.length > 0 || p.setup.completed);
}

/** Pull the case's progress from the server into the local cache. The
 *  first sync for a case on a browser also uploads anything this browser
 *  recorded before progress was kept on the server. Never throws. */
export async function syncCaseProgress(appId?: string): Promise<CaseProgressOut | null> {
  try {
    const id = appId ?? (await caseApplication())?.id;
    if (!id) return null;
    let server = await applications.progress(id);
    const patch: CaseProgressPatch = {};
    let firstSync = false;
    try {
      firstSync = localStorage.getItem(syncedKey(id)) !== "1";
    } catch {
      /* private mode */
    }
    if (firstSync) {
      const extra = readLocalDone().filter((s) => !server.done_stages.includes(s));
      if (extra.length) patch.add_done = extra;
    }
    const localScan = readScanResult();
    if (!server.scan_result && localScan) patch.scan_result = localScan as Record<string, unknown>;
    let localUrl = "";
    try {
      localUrl = localStorage.getItem(SCHOLAR_URL_KEY) || "";
    } catch {
      /* private mode */
    }
    if (!server.scholar_url && localUrl) patch.scholar_url = localUrl;
    if (Object.keys(patch).length) server = await applications.patchProgress(id, patch);
    try {
      localStorage.setItem(syncedKey(id), "1");
    } catch {
      /* private mode */
    }
    writeLocal(server);
    return server;
  } catch {
    return null;
  }
}

/** Record stage changes on the server (fire-and-forget callers ignore the
 *  promise; the local cache was already updated). */
export async function saveStages(add: string[], remove: string[] = []): Promise<CaseProgressOut | null> {
  try {
    const app = await caseApplication();
    if (!app) return null;
    const p = await applications.patchProgress(app.id, { add_done: add, remove_done: remove });
    writeLocal(p);
    return p;
  } catch {
    return null;
  }
}

/** Save a fresh eligibility read to the case, when one exists yet (the
 *  first read runs before sign-up; the Case Plan sync uploads it then). */
export async function saveScan(scan: ScanResult, scholarUrl?: string): Promise<void> {
  try {
    const app = await caseApplication();
    if (!app) return;
    await applications.patchProgress(app.id, {
      scan_result: scan as Record<string, unknown>,
      ...(scholarUrl ? { scholar_url: scholarUrl } : {}),
    });
  } catch {
    /* best-effort */
  }
}

/** Save the timeline nudges (and the day they count from). */
export async function saveTimeline(
  overrides: Record<string, number>,
  start: string,
): Promise<CaseProgressOut | null> {
  try {
    const app = await caseApplication();
    if (!app) return null;
    return await applications.patchProgress(app.id, { milestone_overrides: overrides, timeline_start: start });
  } catch {
    return null;
  }
}

export async function saveSetup(setup: CaseSetup): Promise<CaseProgressOut | null> {
  try {
    const app = await caseApplication();
    if (!app) return null;
    return await applications.patchProgress(app.id, { setup });
  } catch {
    return null;
  }
}
